<template>
    <v-container fluid
                 :class="presentationMode ? 'presentation' : 'normal'"
                 v-if="sessionActive">

        <v-alert
            :value="!hasPermission('view dashboard')"
            type="error"
            class="body-2"
        >
            Your user account doesn't have permission to view dashboard
        </v-alert>

        <div v-if="hasPermission('view dashboard')">

            <keep-alive>

                <div v-if="!presentationMode" style="height: 100%;">

                    <v-speed-dial
                        v-show="!presentationMode"
                        bottom
                        right
                        fixed
                        transition="scale-transition"
                    >
                        <template v-slot:activator>
                            <v-btn
                                color="blue darken-2"
                                dark
                                fab
                            >
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>

                        <!-- FULL SCREEN MODE -->
                        <v-tooltip left color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on"
                                       fab
                                       dark
                                       small
                                       color="primary"
                                       @click="fullScreen(true)">
                                    <v-icon>mdi-fullscreen</v-icon>
                                </v-btn>
                            </template>
                            <span>Go to fullscreen presentation mode</span>
                        </v-tooltip>

                        <!-- ADD WIDGETS BUTTON -->
                        <v-tooltip left color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on"
                                       fab
                                       dark
                                       small
                                       color="primary"
                                       @click="addClicked">
                                    <v-icon>mdi-cog</v-icon>
                                </v-btn>
                            </template>
                            <span>Select widgets</span>
                        </v-tooltip>
                    </v-speed-dial>

                    <!-- DRAGGABLE WIDGET GRID -->
                    <div class="grid" style="height: 1200px;">

                        <!-- GRID ITEMS -->
                        <div v-for="widget in widgets"
                             :key="widget.name"
                             class="item"
                             v-if="widget.active"
                             :data-id="widget.id">

                            <!-- WIDGETS -->
                            <div class="item-content">
                                <keep-alive>
                                    <widget
                                        :key="widget.id"
                                        :widget="widget"
                                        :presentationMode="presentationMode"
                                        @config="widgetChanged"
                                    ></widget>
                                </keep-alive>
                            </div>
                        </div>
                    </div>
                    <json v-if="debug" :data="widgets"></json>
                </div>
            </keep-alive>

            <!-- WIDGETS IN PRESENTATION MODE -->
            <div class="presentation" ref="container" v-if="presentationMode">

                <v-snackbar
                    v-model="snackBar"
                    :timeout="10000"
                    color="primary"
                >
                    <div class="text-center" style="width: 100%;">
                        Hit ESC key to exit the presentation mode
                    </div>
                </v-snackbar>

                <v-container fluid style="height: 100%;">
                    <v-row align="center" justify="space-around" style="height: 100%;">

                        <widget v-for="widget in presentationWidgets"
                                :key="widget.id"
                                :widget="widget"
                                v-if="widget.active && hasPermission(widget.permission)"
                                :presentationMode="presentationMode"
                        ></widget>

                    </v-row>
                </v-container>
            </div>

            <!-- WIDGET SELECTOR DIALOG -->
            <widget-selector
                v-model="widgetSelectorDialogModel"
                @change="widgetChanged"
            ></widget-selector>
        </div>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapActions, mapState} from 'vuex';
import Widget from './Widget';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import WidgetSelector from './WidgetSelectorDialog';
import Muuri from 'muuri';

export default {

    mixins: [activeSessionChecker],

    name: 'DashBoard',

    components: {
        Json,
        Widget,
        WidgetSelector,
    },

    data() {
        return {
            debug: false,
            widgetSelectorDialogModel: false,
            grid: null,
            gridItems: null,
            presentationMode: false,
            gridWidth: null,
            gridCols: null,
            gridMarginY: null,
            snackBar: true,
        }
    },
    computed: {
        ...mapState('DashBoard', [
            'chartData',
            'widgets',
        ]),

        presentationWidgets() {
            if (this.presentationMode) {
                this.calculateGridSize();

                let widgets = this.widgets.filter(widget => {

                    if (this.gridCols === 2) {
                        return widget.order <= 3;
                    } else {
                        return widget.order <= 5;
                    }
                });
                return widgets;
            } else {
                return this.widgets;
            }
        },
    },

    methods: {

        ...mapActions('DashBoard', [
            'updateWidget',
            'getWidgetsConfig',
            'updateWidgetsOrder'
        ]),

        onResize() {
            this.calculateGridSize();
        },

        calculateGridSize() {

            let gridWidth = null;

            if (window.innerWidth >= 1920) {
                this.gridCols = 3;
                gridWidth = 1920;
            } else {
                this.gridCols = 2;
                gridWidth = 1440;
            }

            if (this.presentationMode) {
                let extraHeight = window.innerHeight - 2 * 420;
                let maxMarginY = Math.round(extraHeight / 4);
                if (maxMarginY < 0) maxMarginY = 5;
                this.gridMarginY = maxMarginY + "px";
            }
        },

        addClicked() {
            this.widgetSelectorDialogModel = true;
        },

        widgetChanged(widget) {
            this.updateWidget({name: widget.name, config: widget.config, active: widget.active})
                .then(() => {
                    this.buildMuuriInstance();
                })
        },

        fullScreen(enabled) {
            if (enabled) {

                this.presentationMode = true;

                this.$nextTick(() => {
                    this.$refs.container.requestFullscreen();

                    Bus.$emit('toolbar', false);
                    Bus.$emit('navigation-drawer', false);
                    Bus.$emit('presentation-mode', true);
                    this.calculateGridSize();

                    Bus.$emit('flash-message', {
                        message: 'Press ESC -key to end presentation mode',
                        duration: 10000
                    });

                    document.addEventListener('fullscreenchange', this.exitFullScreen);

                });
            }
        },

        exitFullScreen() {
            if (!document.fullscreenElement) {

                Bus.$emit('toolbar', true);
                Bus.$emit('navigation-drawer', true);
                Bus.$emit('presentation-mode', false);

                this.calculateGridSize();
                this.presentationMode = false;
                this.loadWidgets();
            }
        },

        buildMuuriInstance() {

            const activeFilters = this.widgets.filter(widget => widget.active === true);

            if (activeFilters.length === 0) {
                Bus.$emit('flash-message', {
                    message: 'Your do not have any widgets selected. Click on the blue + button to add some!',
                    duration: 10000
                });
            }

            this.grid = null;

            this.grid = new Muuri('.grid', {
                dragEnabled: true,
                layoutOnResize: true,
                // This causes event not to fire up
                // dragSortPredicate: {
                //     action: "swap",
                //     threshold: 50
                // },
                layout: {
                    fillGaps: true,
                    horizontal: false,
                    alignRight: false,
                    alignBottom: false,
                    rounding: false
                },
            });

            this.grid.on('dragReleaseEnd', items => {

                const order = this.grid.getItems()
                    .map(item => item.getElement()
                        .getAttribute('data-id'));

                this.updateWidgetsOrder(order)
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS: We failed to update your widget order!'
                        });
                    });
            });
        },


        loadWidgets() {

            this.getWidgetsConfig()
                .then(() => {
                    this.buildMuuriInstance();
                })
                .catch((error) => {

                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'We failed to load your widgets. Try reloading the page!'
                    });
                });
        }
    },

    mounted() {

        this.$nextTick(() => {
            window.addEventListener('resize', this.onResize);
        });

        this.loadWidgets();
    },

    created() {
        if (!this.$store.state.DashBoard) {
            this.$store.registerModule('DashBoard', Store);
        }
    },
}

</script>

<style scoped>

.normal {
    padding: 5px;
    height: 100%;
}

.presentation {
    background-color: #414141;
    padding: 5px;
    height: 100%;
}

.grid {
    position: relative;
    width: 100%;
    height: 100%;
}

.item {
    display: block;
    position: absolute;
    width: 550px;
    height: 420px;
    line-height: 400px;
    z-index: 1;
    margin: 5px;
}

.item.muuri-item-hidden {
    z-index: 0;
}

.item.muuri-item-positioning {
    z-index: 2;
}

.item.muuri-item-releasing {
    z-index: 3;
}

.item.muuri-item-dragging {
    z-index: 4;
}

.item-content {
    position: relative;
    width: 100%;
    height: 100%;
    text-align: center;
    font-size: 50px;
    color: white;
    cursor: pointer;
}

.item.muuri-item-dragging .item-content {
    box-shadow: 0px 0px 10px 5px #329EF4;
}

.item.muuri-item-releasing .item-content {

}
</style>